import FroalaEditor from 'froala-editor/js/froala_editor.min.js';
import api from '../../../../../api/api';
import { MUTATION_SPELL_CHECK, QUERY_SPELL_CHECK_STATUS } from '../../../../../api/spell_checker_api';

const callApi = async (text, onProgress) => {
  try {
    const response = await api.graphql.mutate({
      mutation: MUTATION_SPELL_CHECK,
      variables: { input_text: text },
    });

    const result = response?.data?.SpellCheck;

    if (result?.statusCode === 200) {
      if (onProgress) onProgress({ status: 'SUBMITTED', progress: 10 });
      const text_result = await jobPollingWithProgress(result.data.jobId, onProgress);
      return text_result;
    }

    throw new Error(`API returned status ${result?.statusCode}`);
  } catch (error) {
    console.error('API call error:', error);
    throw error;
  }
};

const jobPollingWithProgress = async (jobId, onProgress) => {
  try {
    let attempts = 0;
    const maxAttempts = 60;

    while (attempts < maxAttempts) {
      const response = await api.graphql.query({
        query: QUERY_SPELL_CHECK_STATUS,
        variables: { job_id: jobId },
        fetchPolicy: 'no-cache',
      });

      const _result = response?.data?.SpellCheckJob?.data;

      if (onProgress) {
        const progress = Math.min(90, 10 + (attempts / maxAttempts) * 80);
        onProgress({ status: _result.status === 'COMPLETED' ? 'COMPLETED' : 'PROCESSING', progress });
      }

      if (_result.status === 'COMPLETED') {
        if (onProgress) onProgress({ status: 'COMPLETED', progress: 100 });
        return _result.result;
      }

      if (_result.status === 'ERROR') throw new Error('Spell check job failed');

      await new Promise((r) => setTimeout(r, 1000));
      attempts++;
    }

    throw new Error('Spell check job timed out');
  } catch (err) {
    console.error('Job polling error:', err);
    alert('เกิดข้อผิดพลาดในการตรวจสอบคำสะกด กรุณาลองใหม่อีกครั้ง');
    throw err;
  }
};

const spellcheckFinishIcon = `<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 64 64"
  width="24" height="24"
  role="img" aria-label="Thai ก with check mark"
  style="display:inline-block;vertical-align:middle"
>
  <!-- Letter ก -->
  <text
    x="12" y="50"
    font-size="60"
    font-weight="700"
    fill="#000"
    font-family="Bai Jamjuree, 'Noto Sans Thai', 'Sarabun', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif"
  >ถ</text>

  <!-- Green check mark bottom-right -->
  <path
    d="M35 48 l8 8 l16 -30"
    stroke="#28a745"
    stroke-width="10"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`
const spellcheckIcon = `<svg
  xmlns="http://www.w3.org/2000/svg"
  viewBox="0 0 64 48"
  width="24" height="24"
  role="img" aria-label="Thai ก with red wavy underline"
  style="display:inline-block;vertical-align:middle"
>
  <!-- Letter ก: bold, black fill -->
  <text
    x="20" y="38"
    text-anchor="start"
    font-size="50"
    font-weight="500"
    fill="#000"
    font-family="Bai Jamjuree, 'Noto Sans Thai', 'Sarabun', system-ui, -apple-system, 'Segoe UI', Arial, sans-serif"
    dominant-baseline="alphabetic"
  >ต</text>

  <!-- Red wavy underline (spellcheck-like) -->
  <path
    d="M8 50
       c 2 -4 6 -4 8 0
       s 6 4 8 0
       s 6 -4 8 0
       s 6 4 8 0
       s 6 -4 8 0
       s 6 4 8 0"
    fill="none"
    stroke="#ff0000"
    stroke-width="4"
    stroke-linecap="round"
    stroke-linejoin="round"
  />
</svg>`

const spellcheckingIcon = `<svg class="fr-spinner" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
  <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
</svg>`

// Inject minimal styles for incorrect words (idempotent)
const injectStyles = () => {
  if (document.getElementById('fr-spellchecker-style')) return;
  const style = document.createElement('style');
  style.id = 'fr-spellchecker-style';
  style.innerHTML = `
    .fr-spell-incorrect {
      text-decoration-line: underline;
      text-decoration-color: red;
      text-decoration-style: wavy;
      cursor: pointer;
    }
    .fr-spell-incorrect.changed {
      text-decoration-color: green;
    }

    .fr-command[data-cmd="spellchecker"].fr-spell-active {
      background-color: rgba(255, 235, 238, 0.9) !important;
    }

    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }

    .fr-spinner {
      animation: spin 1s linear infinite;
    }

      /* Spellchecker suggestion tooltip */
      .fr-spell-tooltip {
        position: fixed;
        pointer-events: none;
        background: #EDF2F7;
        color: #262626ff;
        padding: 6px 8px;
        font-size: 16px;
        border-radius: 4px;
        box-shadow: 0 4px 10px rgba(0,0,0,0.25);
        z-index: 99999;
        border: 1px solid #E2E8F0;
        white-space: nowrap;
        transform-origin: top left;
        transition: opacity 120ms ease, transform 120ms ease;
        opacity: 0;
        transform: translateY(4px) scale(0.98);
      }

      .fr-spell-tooltip.fr-visible {
        opacity: 1;
        transform: translateY(0) scale(1);
      }
    .fr-diff-removed { color: #e53935; background-color: rgba(229,57,53,0.06); padding: 0 2px; border-radius: 2px; }
    .fr-diff-added { color: #2e7d32; background-color: rgba(46,125,50,0.06); padding: 0 2px; border-radius: 2px; }
  .fr-diff-arrow { margin-top: 4px; font-size: 18px; color: #334155; opacity: 0.9; white-space: nowrap; }
  `;
  document.head.appendChild(style);
};

// Helper: compute a simple char-level diff and return HTML with
// removed parts wrapped in <span class="fr-diff-removed">...</span>
// and added parts wrapped in <span class="fr-diff-added">...</span>
// This is a small, fast diff suitable for short words (Thai text ok).
// default diff options (can be overridden per-editor via `editor.opts.spellcheckerDiff`)
const defaultDiffOptions = {
  granularity: 'grapheme', // 'char' or 'grapheme'
  showArrow: true // show simple `orig → corr` line under the inline diff
};

// makeInlineDiffHtml accepts an optional splitter function as third arg
// splitter(s) => array of segments
const makeInlineDiffHtml = (oldStr, newStr, splitter) => {
  if (!oldStr) return `<span class="fr-diff-added">${escapeHtml(newStr)}</span>`;
  if (!newStr) return `<span class="fr-diff-removed">${escapeHtml(oldStr)}</span>`;

  // Simple dynamic programming LCS to find longest common subsequence
  const a = splitter ? splitter(oldStr) : Array.from(oldStr);
  const b = splitter ? splitter(newStr) : Array.from(newStr);
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? 1 + dp[i + 1][j + 1] : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const parts = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      parts.push(escapeHtml(a[i]));
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      // a[i] is removed
      let buf = '';
      while (i < m && (j >= n || dp[i + 1][j] >= dp[i][j + 1])) {
        buf += a[i++];
      }
      parts.push(`<span class="fr-diff-removed">${escapeHtml(buf)}</span>`);
    } else {
      // b[j] is added
      let buf = '';
      while (j < n && (i >= m || dp[i][j + 1] > dp[i + 1][j])) {
        buf += b[j++];
      }
      parts.push(`<span class="fr-diff-added">${escapeHtml(buf)}</span>`);
    }
  }
  // trailing removals
  if (i < m) {
    parts.push(`<span class="fr-diff-removed">${escapeHtml(a.slice(i).join(''))}</span>`);
  }
  // trailing additions
  if (j < n) {
    parts.push(`<span class="fr-diff-added">${escapeHtml(b.slice(j).join(''))}</span>`);
  }
  return parts.join('');
};

// Build a parallel single-line diff: left side shows removed parts in red,
// right side shows added parts in green, separated by an arrow.
const makeParallelDiffHtml = (oldStr, newStr, splitter) => {
  if (!oldStr && !newStr) return '';
  if (!oldStr) return `<span class="fr-diff-removed"></span> <span class="fr-diff-arrow">→</span> <span class="fr-diff-added">${escapeHtml(newStr)}</span>`;
  if (!newStr) return `<span class="fr-diff-removed">${escapeHtml(oldStr)}</span> <span class="fr-diff-arrow">→</span> <span class="fr-diff-added"></span>`;

  const a = splitter ? splitter(oldStr) : Array.from(oldStr);
  const b = splitter ? splitter(newStr) : Array.from(newStr);
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = m - 1; i >= 0; i--) {
    for (let j = n - 1; j >= 0; j--) {
      dp[i][j] = a[i] === b[j] ? 1 + dp[i + 1][j + 1] : Math.max(dp[i + 1][j], dp[i][j + 1]);
    }
  }

  const leftParts = [];
  const rightParts = [];
  let i = 0;
  let j = 0;
  while (i < m && j < n) {
    if (a[i] === b[j]) {
      leftParts.push(escapeHtml(a[i]));
      rightParts.push(escapeHtml(a[i]));
      i++; j++;
    } else if (dp[i + 1][j] >= dp[i][j + 1]) {
      // removals from a
      let buf = '';
      while (i < m && (j >= n || dp[i + 1][j] >= dp[i][j + 1])) {
        buf += a[i++];
      }
      leftParts.push(`<span class="fr-diff-removed">${escapeHtml(buf)}</span>`);
    } else {
      // additions from b
      let buf = '';
      while (j < n && (i >= m || dp[i][j + 1] > dp[i + 1][j])) {
        buf += b[j++];
      }
      rightParts.push(`<span class="fr-diff-added">${escapeHtml(buf)}</span>`);
    }
  }
  // trailing removals
  if (i < m) {
    leftParts.push(`<span class="fr-diff-removed">${escapeHtml(a.slice(i).join(''))}</span>`);
  }
  // trailing additions
  if (j < n) {
    rightParts.push(`<span class="fr-diff-added">${escapeHtml(b.slice(j).join(''))}</span>`);
  }

  return `${leftParts.join('')} <span class="fr-diff-arrow">→</span> ${rightParts.join('')}`;
};

const escapeHtml = (s) => {
  return String(s)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

injectStyles();

// Create a single tooltip element to reuse
const getTooltip = (() => {
  let tip = null;
  return () => {
    if (tip) return tip;
    tip = document.createElement('div');
    tip.className = 'fr-spell-tooltip';
    tip.setAttribute('role', 'tooltip');
    tip.style.top = '0px';
    tip.style.left = '0px';
    document.body.appendChild(tip);
    return tip;
  };
})();

// Helper to safely parse and process returned HTML from API
const processResultHtml = (resultHtml) => {
  const container = document.createElement('div');
  container.innerHTML = resultHtml;

  const spans = container.querySelectorAll('span[data-original][data-corrected]');
  spans.forEach((s) => {
    const orig = s.getAttribute('data-original');
    const corr = s.getAttribute('data-corrected');

    // keep attributes for toggling/cleanup
    s.setAttribute('contenteditable', 'true');
    s.classList.add('fr-spell-incorrect');

    // show original word (so user sees the incorrect text highlighted)
    if (orig != null) s.textContent = orig;

    // Keep attributes intact (data-original/data-corrected) for toggling
  });

  return container.innerHTML;
};

// Register Froala commands/icons
try {
  FroalaEditor.RegisterCommand('spellchecker', {
    title: 'ตรวจคำ',
    icon: spellcheckIcon,
    focus: false,
    undo: true,
    refreshAfterCallback: true,
    callback: async function () {
      const editor = this;
      editor._spell = editor._spell || {};

      // If spellcheck is already active, treat this click as Finish (end-process)
      if (editor._spell.active) {
        // Invoke the same cleanup logic as the finish action: remove handlers and replace spans with text
        try {
          const toggleHandler = editor._spell._toggleHandler;
          const inputHandler = editor._spell._inputHandler;
          if (editor.$el && editor.$el.off) {
            if (toggleHandler) editor.$el.off('click', 'span.fr-spell-incorrect', toggleHandler);
            if (inputHandler) editor.$el.off('input', 'span.fr-spell-incorrect', inputHandler);
            // mouse handlers
            if (editor._spell._mouseOverHandler) editor.$el.off('mouseover', 'span.fr-spell-incorrect', editor._spell._mouseOverHandler);
            if (editor._spell._mouseMoveHandler) editor.$el.off('mousemove', 'span.fr-spell-incorrect', editor._spell._mouseMoveHandler);
            if (editor._spell._mouseOutHandler) editor.$el.off('mouseout', 'span.fr-spell-incorrect', editor._spell._mouseOutHandler);
          } else if (editor.el) {
            if (toggleHandler) editor.el.removeEventListener('click', toggleHandler);
            if (inputHandler) editor.el.removeEventListener('input', inputHandler);
            if (editor._spell._mouseOverHandler) editor.el.removeEventListener('mouseover', editor._spell._mouseOverHandler);
            if (editor._spell._mouseMoveHandler) editor.el.removeEventListener('mousemove', editor._spell._mouseMoveHandler);
            if (editor._spell._mouseOutHandler) editor.el.removeEventListener('mouseout', editor._spell._mouseOutHandler);
          }

          // Replace annotated spans with their current text content
          const html = editor.html.get();
          const container = document.createElement('div');
          container.innerHTML = html;

          const spans = container.querySelectorAll('span[data-original]');
          spans.forEach((s) => {
            const text = s.textContent || '';
            const textNode = document.createTextNode(text);
            s.parentNode.replaceChild(textNode, s);
          });

          if (editor.$el && editor.$el.find) {
            const $editable = editor.$el.find('.fr-element');
            if ($editable && $editable.length) {
              $editable.html(container.innerHTML);
            } else if (editor.el) {
              editor.el.innerHTML = container.innerHTML;
            }
          } else if (editor.el) {
            editor.el.innerHTML = container.innerHTML;
          }
          editor.events.trigger('contentChanged');
          if (editor.undo && editor.undo.saveStep) editor.undo.saveStep();
        } catch (err) {
          console.error('Failed to finish spellcheck', err);
        }

        // Remove active state and restore original icon
        const $btn = editor.$box ? editor.$box.find('.fr-command[data-cmd="spellchecker"]') : null;
        if ($btn && $btn.length) {
          $btn.removeClass('fr-spell-active');
          $btn.attr('title', 'Spellcheck');
          $btn.attr('aria-label', 'Spellcheck');
          // Update icon back to check
          $btn.html(spellcheckIcon);
        }
        // hide tooltip if visible
        try {
          const tip = getTooltip();
          if (tip) tip.classList.remove('fr-visible');
        } catch (e) {
          // ignore
        }

        editor._spell = {};
        editor.events.trigger('spellchecker.finished');
        return;
      }

      // Otherwise start spellcheck run
      // Prevent double runs
      if (editor._spell.loading) return;
      editor._spell.loading = true;

      // Show loading state
      const $btn = editor.$box ? editor.$box.find('.fr-command[data-cmd="spellchecker"]') : null;
      if ($btn && $btn.length) {
        $btn.attr('title', 'กำลังตรวจสอบ...');
        $btn.attr('aria-label', 'กำลังตรวจสอบ...');
        // Update icon to spinner with spinning class
        $btn.html(spellcheckingIcon);
      }

      try {
        // Get current HTML (preserve structure)
        const html = editor.html.get();

        // Call API (callApi returns HTML string with spans)
        const resultHtml = await callApi(html, (progress) => {
          // optional: could show progress somewhere; we just leave this hook
          editor.events.trigger('spellchecker.progress', [progress]);
        });

        // Process the returned HTML: show original text but keep data attrs
        const processed = processResultHtml(resultHtml);

        // Check if there are any incorrect words (spans with data-original attribute)
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = processed;
        const incorrectWords = tempContainer.querySelectorAll('span[data-original]');

        // If no incorrect words found, show popup and restore icon
        if (incorrectWords.length === 0) {
          // Show popup message
          alert('ไม่พบคำผิด การตรวจสอบคำสะกดเสร็จสมบูรณ์');

          // Reset button state to default
          if ($btn && $btn.length) {
            $btn.removeClass('fr-spell-active');
            $btn.attr('title', 'ตรวจคำ');
            $btn.attr('aria-label', 'ตรวจคำ');
            $btn.html(spellcheckIcon);
          }

          editor._spell.loading = false;
          editor.events.trigger('spellchecker.noErrors');
          return;
        }

        // Ensure allowed attrs include our data attributes (defensive in case options differ)
        try {
          if (editor.opts) {
            editor.opts.htmlAllowedAttrs = editor.opts.htmlAllowedAttrs || [];
            if (!editor.opts.htmlAllowedAttrs.includes('data-original')) editor.opts.htmlAllowedAttrs.push('data-original');
            if (!editor.opts.htmlAllowedAttrs.includes('data-corrected')) editor.opts.htmlAllowedAttrs.push('data-corrected');
          }
        } catch (e) {
          // ignore
        }

        // Set editor content directly into the editable element to avoid Froala's sanitizer
        // which may strip custom data-* attributes. Then notify Froala about the change.
        if (editor.$el && editor.$el.find) {
          const $editable = editor.$el.find('.fr-element');
          if ($editable && $editable.length) {
            $editable.html(processed);
          } else if (editor.el) {
            editor.el.innerHTML = processed;
          }
        } else if (editor.el) {
          editor.el.innerHTML = processed;
        }
        // Inform Froala that content changed and save undo step
        editor.events.trigger('contentChanged');
        if (editor.undo && editor.undo.saveStep) editor.undo.saveStep();

        // Attach delegated click handler for toggling between original/corrected
        // Use namespaced handler so it can be removed later
        const toggleHandler = function (e) {
          const target = e.target;
          if (target && target.nodeType === 1 && target.matches('span.fr-spell-incorrect')) {
            const orig = target.getAttribute('data-original');
            const corr = target.getAttribute('data-corrected');
            if (!orig && !corr) return;

            // toggle based on current text
            if (target.textContent === orig) {
              if (corr != null) target.textContent = corr;
            } else {
              if (orig != null) target.textContent = orig;
            }
            target.classList.add('changed');

            // let Froala know content changed
            editor.events.trigger('contentChanged');
            // If tooltip is visible, update it to show the alternate word
            try {
              const tip = getTooltip();
              tip.classList.remove('fr-visible');
            } catch (err) {
              // ignore tooltip update errors
            }
          }
        };

        // input handler to allow manual edits: mark as edited by removing data-corrected if changed
        const inputHandler = function (e) {
          const target = e.target;
          if (target && target.nodeType === 1 && target.matches('span.fr-spell-incorrect')) {
            const orig = target.getAttribute('data-original');
            const corr = target.getAttribute('data-corrected');
            // if user changed text to something that doesn't match either, drop corrected attribute
            if (target.textContent !== orig && target.textContent !== corr) {
              // keep the original attribute but remove data-corrected to indicate manual edit
              target.removeAttribute('data-corrected');
              // hide tooltip when user manually edits
              try {
                const tip = getTooltip();
                if (tip) tip.classList.remove('fr-visible');
              } catch (err) {
                // ignore
              }
            }
            editor.events.trigger('contentChanged');
          }
        };

        // Store handlers for cleanup
        editor._spell._toggleHandler = toggleHandler;
        editor._spell._inputHandler = inputHandler;

        // Tooltip handlers: show suggestion on hover and follow the mouse
        const tip = getTooltip();

        const mouseOverHandler = function (e) {
          const target = e.target;
          if (!(target && target.nodeType === 1 && target.matches('span.fr-spell-incorrect'))) return;
          const orig = target.getAttribute('data-original') || '';
          const corr = target.getAttribute('data-corrected') || '';
          const current = target.textContent || '';

          // If both orig and corr blank, nothing to show
          if (!orig && !corr) {
            if (tip) tip.classList.remove('fr-visible');
            return;
          }

          // Determine what to show: if current equals orig, show diff orig->corr, else show diff corr->orig
          let from = orig;
          let to = corr;
          if (current !== orig) {
            // show the alternate (original) as 'to'
            from = corr;
            to = orig;
          }

          // Allow per-editor override of diff options
          let diffOptions = defaultDiffOptions;
          try {
            if (editor && editor.opts && editor.opts.spellcheckerDiff) {
              diffOptions = Object.assign({}, defaultDiffOptions, editor.opts.spellcheckerDiff);
            }
          } catch (err) {
            // ignore
          }

          // choose splitter: grapheme clusters or char (Array.from is ok for many cases, grapheme segmentation
          // gives better results for complex scripts; fallback to Array.from)
          let splitter = null;
          if (diffOptions.granularity === 'grapheme' && typeof Intl !== 'undefined' && Intl.Segmenter) {
            const seg = new Intl.Segmenter(undefined, { granularity: 'grapheme' });
            splitter = (s) => Array.from(seg.segment(s), segItem => segItem.segment);
          }

          // Build a single-line parallel diff: left (old) with red removed spans,
          // arrow, then right (new) with green added spans.
          let mainHtml = makeParallelDiffHtml(from || '', to || '', splitter);
          tip.innerHTML = mainHtml;

          tip.classList.add('fr-visible');
          // position will be adjusted on mousemove
        };

        const mouseMoveHandler = function (e) {
          if (!tip || !tip.classList.contains('fr-visible')) return;
          // small offset so tooltip doesn't cover cursor
          const offsetX = 12;
          const offsetY = 16;
          let x = e.clientX + offsetX;
          let y = e.clientY + offsetY;

          // keep inside viewport
          const rect = tip.getBoundingClientRect();
          const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
          const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
          if (x + rect.width + 8 > vw) x = vw - rect.width - 8;
          if (y + rect.height + 8 > vh) y = vh - rect.height - 8;

          tip.style.left = `${x}px`;
          tip.style.top = `${y}px`;
        };

        const mouseOutHandler = function (e) {
          const target = e.target;
          if (!(target && target.nodeType === 1 && target.matches('span.fr-spell-incorrect'))) return;
          if (tip) tip.classList.remove('fr-visible');
        };

        editor._spell._mouseOverHandler = mouseOverHandler;
        editor._spell._mouseMoveHandler = mouseMoveHandler;
        editor._spell._mouseOutHandler = mouseOutHandler;

        if (editor.$el && editor.$el.on) {
          editor.$el.on('mouseover', 'span.fr-spell-incorrect', mouseOverHandler);
          editor.$el.on('mousemove', 'span.fr-spell-incorrect', mouseMoveHandler);
          editor.$el.on('mouseout', 'span.fr-spell-incorrect', mouseOutHandler);
        } else if (editor.el) {
          editor.el.addEventListener('mouseover', mouseOverHandler);
          editor.el.addEventListener('mousemove', mouseMoveHandler);
          editor.el.addEventListener('mouseout', mouseOutHandler);
        }

        // Use Froala's jQuery-wrapped editor element for delegated events if available
        if (editor.$el && editor.$el.on) {
          editor.$el.on('click', 'span.fr-spell-incorrect', toggleHandler);
          editor.$el.on('input', 'span.fr-spell-incorrect', inputHandler);
        } else if (editor.el) {
          // Fallback to event listeners on the editable element
          editor.el.addEventListener('click', toggleHandler);
          editor.el.addEventListener('input', inputHandler);
        }

        // Mark active state and update icon
        if ($btn && $btn.length) {
          $btn.addClass('fr-spell-active');
          $btn.attr('title', 'ตรวจสอบเสร็จสิ้น');
          $btn.attr('aria-label', 'ตรวจสอบเสร็จสิ้น');
          // Remove spinning animation and update icon to times
          $btn.html(spellcheckFinishIcon);
        }

        editor._spell.active = true;
      } catch (err) {
        console.error('Spellcheck failed', err);
        editor.events.trigger('spellchecker.error', [err]);

        // Reset button state on error
        const $btn = editor.$box ? editor.$box.find('.fr-command[data-cmd="spellchecker"]') : null;
        if ($btn && $btn.length) {
          $btn.attr('title', 'Spellcheck');
          $btn.attr('aria-label', 'Spellcheck');
          $btn.html(spellcheckIcon);
        }

        // cleanup tooltip and handlers on error
        try {
          if (editor.$el && editor.$el.off) {
            if (editor._spell._mouseOverHandler) editor.$el.off('mouseover', 'span.fr-spell-incorrect', editor._spell._mouseOverHandler);
            if (editor._spell._mouseMoveHandler) editor.$el.off('mousemove', 'span.fr-spell-incorrect', editor._spell._mouseMoveHandler);
            if (editor._spell._mouseOutHandler) editor.$el.off('mouseout', 'span.fr-spell-incorrect', editor._spell._mouseOutHandler);
          } else if (editor.el) {
            if (editor._spell._mouseOverHandler) editor.el.removeEventListener('mouseover', editor._spell._mouseOverHandler);
            if (editor._spell._mouseMoveHandler) editor.el.removeEventListener('mousemove', editor._spell._mouseMoveHandler);
            if (editor._spell._mouseOutHandler) editor.el.removeEventListener('mouseout', editor._spell._mouseOutHandler);
          }
          const tip = getTooltip();
          if (tip) tip.classList.remove('fr-visible');
        } catch (e) {
          // ignore
        }
      } finally {
        editor._spell.loading = false;
      }
    }
  });

  // NOTE: Single 'spellchecker' button now toggles between run and finish using proper Froala icon management.
} catch (e) {
  console.error('Failed to register Froala spellchecker commands', e);
}